/**
 * The buyer's likes, interest, comments and sheet note on a branded line
 * sheet (KP-4572), one store per sheet.
 *
 * Every action applies at once and joins a queue kept in this browser; the
 * queue is sent in batches after a short pause (300 ms for a toggle, 1.5 s for
 * typing) and retried after 1, 2, 4 and 8 s, then on the next load or when the
 * browser comes back online. The page never waits on any of it: when feedback
 * is unavailable the queue simply stays on this device.
 *
 * Who the feedback is from: a buyer who came through a tracked email link is
 * that retailer and is never asked. Anyone else is asked once, on their first
 * action, for an email and store name, remembered for every sheet. "Not you?"
 * stops this browser speaking as the email link's retailer on this sheet.
 */

import Vue from 'vue'
import { cleanBlock } from './feedbackText'
import {
  BLANK_ITEM,
  NOTE_MAX,
  batchItems,
  capPending,
  itemsFromReactions,
  loadPending,
  mergeByRev,
  mergeNote,
  nextRev,
  savePending,
  toNote,
  wireItem,
} from './feedbackQueue'
import {
  cleanIdentity,
  ignoresTrackedLink,
  isEmailLike,
  saveIdentity,
  setIgnoresTrackedLink,
  storedIdentity,
  visitorKey,
} from './visitorIdentity'

export {
  mergeByRev, nextRev, batchItems, pendingKey,
} from './feedbackQueue'

export const TOGGLE_DELAY_MS = 300
export const TEXT_DELAY_MS = 1500
export const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000]
// "Not now" keeps the action local; the next action asks once more, then
// the visit is left alone.
const MAX_DECLINES = 2

export const FEEDBACK_EVENTS = Object.freeze({
  liked: 'Line Sheet Feedback Liked',
  interested: 'Line Sheet Feedback Interested',
  commented: 'Line Sheet Feedback Commented',
  noteSaved: 'Line Sheet Feedback Note Saved',
  identified: 'Line Sheet Feedback Identified',
})

const DEFAULT_LIMITS = Object.freeze({ comment: 1000, note: 2000, store_name: 120 })
const isRetryable = status => status === 0 || status === 429 || status >= 500

function identifyError(res) {
  if (res.status === 429) return 'rate_limited'
  const error = res.data && typeof res.data.error === 'string' ? res.data.error : ''
  return error || 'network'
}

const firstTime = (before, after) => !before && Boolean(after)

function itemEvents(prev, next) {
  return [
    firstTime(prev.liked, next.liked) && FEEDBACK_EVENTS.liked,
    firstTime(prev.interested, next.interested) && FEEDBACK_EVENTS.interested,
    firstTime(cleanBlock(prev.comment, 1), cleanBlock(next.comment, 1)) && FEEDBACK_EVENTS.commented,
  ].filter(Boolean)
}

const rejectedKept = reason => reason === 'not_on_sheet' || reason === 'cap'

export function createLineSheetFeedback({
  token,
  trackedLinkId = '',
  api,
  now = () => Date.now(),
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = id => clearTimeout(id),
  onEvent = () => {},
}) {
  const state = Vue.observable({
    available: false,
    ready: false,
    viewer: null,
    items: {},
    note: { text: '', rev: 0 },
    limits: DEFAULT_LIMITS,
    status: 'idle',
    prompt: false,
  })

  let pending = loadPending(token)
  let held = new Set()
  let declines = 0
  let retryIndex = 0
  let failing = false
  let flushTimer = null
  let flushAt = Infinity
  let running = null
  let destroyed = false

  const via = () => (state.viewer ? state.viewer.via : 'none')
  // Analytics is a side channel: a failure there never stops a save.
  const emit = name => {
    try {
      onEvent(name, { via: via() })
    } catch (e) {
      // Ignored on purpose.
    }
  }
  const tl = () => (ignoresTrackedLink(token) ? '' : trackedLinkId || '')
  const who = () => ({ tl: tl(), vk: visitorKey() })
  const heldKey = (id, rev) => `${id}:${rev}`

  function persist() {
    pending = { items: capPending(pending.items), note: pending.note }
    savePending(token, pending)
  }

  const sendableItems = () => Object.entries(pending.items).filter(([id, item]) => !held.has(heldKey(id, item.rev)))
  const sendableNote = () => (pending.note && !held.has(heldKey('note', pending.note.rev)) ? pending.note : null)
  const hasSendable = () => sendableItems().length > 0 || Boolean(sendableNote())
  const hasPending = () => Object.keys(pending.items).length > 0 || Boolean(pending.note)

  function settleStatus() {
    if (failing) state.status = 'offline'
    else if (hasPending()) state.status = 'local'
    else if (state.status !== 'idle') state.status = 'saved'
  }

  function applyState(data) {
    state.viewer = data && data.viewer && typeof data.viewer === 'object' ? { ...data.viewer } : null
    const merged = mergeByRev(itemsFromReactions(data && data.reactions), pending.items)
    const note = mergeNote(toNote(data && data.note), pending.note)
    state.items = merged.items
    state.note = note.note
    state.limits = { ...DEFAULT_LIMITS, ...((data && data.limits) || {}) }
    pending = { items: merged.pending, note: note.pending }
    persist()
  }

  function clearTimer() {
    if (flushTimer !== null) cancel(flushTimer)
    flushTimer = null
    flushAt = Infinity
  }

  function scheduleFlush(ms, options) {
    if (destroyed) return
    const at = now() + ms
    if (flushTimer !== null && flushAt <= at && !options.reset) return
    clearTimer()
    flushAt = at
    flushTimer = schedule(() => {
      flushTimer = null
      flushAt = Infinity
      flush(options)
    }, ms)
  }

  function scheduleRetry(res) {
    failing = true
    if (retryIndex >= RETRY_DELAYS_MS.length) return
    const delay = Math.max(RETRY_DELAYS_MS[retryIndex], (res.retryAfter || 0) * 1000)
    retryIndex += 1
    scheduleFlush(delay, { mayPrompt: false, reset: true })
  }

  async function identifyWith(identity) {
    const clean = cleanIdentity(identity)
    if (!isEmailLike(clean.email)) return { ok: false, error: 'bad_request', field: 'email' }
    const res = await api.identify({ vk: visitorKey(), ...clean })
    if (!res.ok) {
      const field = res.data && typeof res.data.field === 'string' ? res.data.field : ''
      return { ok: false, error: identifyError(res), field }
    }
    saveIdentity(clean)
    applyState(res.data)
    state.prompt = false
    emit(FEEDBACK_EVENTS.identified)
    return { ok: true, error: '', field: '' }
  }

  // True once the visitor is known. Asks only on the visitor's own action.
  async function ensureIdentity(mayPrompt) {
    if (state.viewer) return true
    const remembered = storedIdentity()
    if (remembered && (await identifyWith(remembered)).ok) return true
    if (mayPrompt && declines < MAX_DECLINES) state.prompt = true
    return false
  }

  function applyResults(data) {
    const ack = list => (Array.isArray(list) ? list : []).filter(a => a && typeof a.product_id === 'string')
    const items = { ...pending.items }
    ack(data.applied).concat(ack(data.stale)).forEach(a => {
      if (items[a.product_id] && items[a.product_id].rev === a.rev) delete items[a.product_id]
    })
    ack(data.rejected).forEach(r => {
      const item = items[r.product_id]
      if (!item) return
      if (rejectedKept(r.reason)) held.add(heldKey(r.product_id, item.rev))
      else delete items[r.product_id]
    })
    pending = { ...pending, items }
    persist()
  }

  const holdAll = entries => entries.forEach(([id, item]) => held.add(heldKey(id, item.rev)))

  // One request's outcome: 'ok', 'retry', 'identify' or 'held'.
  function outcome(res, onOk, onHold) {
    if (res.ok) {
      onOk(res.data)
      return 'ok'
    }
    if (res.status === 401) {
      state.viewer = null
      return 'identify'
    }
    if (isRetryable(res.status)) {
      scheduleRetry(res)
      return 'retry'
    }
    onHold()
    return 'held'
  }

  async function sendItems() {
    const entries = sendableItems()
    const batches = batchItems(entries.map(([id, item]) => wireItem(id, item)))
    for (const batch of batches) {
      const entriesOf = entries.filter(([id]) => batch.some(b => b.product_id === id))
      // eslint-disable-next-line no-await-in-loop
      const res = await api.reactions({ ...who(), items: batch })
      const result = outcome(res, applyResults, () => holdAll(entriesOf))
      if (result === 'retry' || result === 'identify') return result
    }
    return 'ok'
  }

  async function sendNote() {
    const note = sendableNote()
    if (!note) return 'ok'
    const res = await api.note({ ...who(), text: cleanBlock(note.text, NOTE_MAX), rev: note.rev })
    return outcome(res, () => {
      if (pending.note && pending.note.rev === note.rev) pending = { ...pending, note: null }
      persist()
    }, () => held.add(heldKey('note', note.rev)))
  }

  // One re-identify per flush: a server that keeps answering identify first
  // leaves the feedback on this device instead of looping.
  async function sendAll(mayPrompt, reidentified = false) {
    if (!(await ensureIdentity(mayPrompt))) return
    state.status = 'saving'
    const items = await sendItems()
    const result = items === 'ok' ? await sendNote() : items
    if (result === 'identify') {
      if (!reidentified && await ensureIdentity(mayPrompt)) return sendAll(mayPrompt, true)
      return
    }
    if (result !== 'retry') {
      failing = false
      retryIndex = 0
    }
  }

  async function run(options) {
    if (state.available && hasSendable()) await sendAll(Boolean(options.mayPrompt))
    settleStatus()
  }

  // One flush at a time; a call meanwhile runs after the one in flight.
  function flush(options = {}) {
    clearTimer()
    if (destroyed) return Promise.resolve()
    const next = (running || Promise.resolve()).then(() => run(options))
    const release = () => { if (running === next) running = null }
    running = next
    next.then(release, release)
    return next
  }

  async function load() {
    held = new Set()
    const res = await api.state(who())
    state.ready = true
    if (destroyed) return
    if (!res.ok) {
      state.available = false
      failing = res.status === 0
      settleStatus()
      return
    }
    state.available = true
    applyState(res.data)
    await flush({ mayPrompt: false })
  }

  function setItem(productId, patch) {
    const prev = state.items[productId] || BLANK_ITEM
    const next = { ...prev, ...patch, rev: nextRev(prev.rev, now()) }
    state.items = { ...state.items, [productId]: next }
    pending = { ...pending, items: { ...pending.items, [productId]: next } }
    persist()
    itemEvents(prev, next).forEach(emit)
    if (!state.available) state.status = 'local'
    const typing = Object.prototype.hasOwnProperty.call(patch, 'comment')
    scheduleFlush(typing ? TEXT_DELAY_MS : TOGGLE_DELAY_MS, { mayPrompt: true, reset: typing })
  }

  function setNote(text) {
    const prev = state.note
    const next = { text: String(text || ''), rev: nextRev(prev.rev, now()) }
    state.note = next
    pending = { ...pending, note: next }
    persist()
    if (firstTime(cleanBlock(prev.text, 1), cleanBlock(next.text, 1))) emit(FEEDBACK_EVENTS.noteSaved)
    if (!state.available) state.status = 'local'
    scheduleFlush(TEXT_DELAY_MS, { mayPrompt: true, reset: true })
  }

  async function identify(identity) {
    const out = await identifyWith(identity)
    if (out.ok) await flush({ mayPrompt: false })
    return out
  }

  function declineIdentity() {
    state.prompt = false
    declines += 1
    settleStatus()
  }

  async function notMe() {
    setIgnoresTrackedLink(token)
    declines = 0
    state.viewer = null
    state.items = {}
    await load()
    if (!state.viewer && state.available) await ensureIdentity(true)
  }

  function changeIdentity() {
    state.prompt = true
  }

  function itemOf(productId) {
    return state.items[productId] || BLANK_ITEM
  }

  function online() {
    failing = false
    retryIndex = 0
    return state.available ? flush({ mayPrompt: false }) : load()
  }

  function destroy() {
    destroyed = true
    clearTimer()
  }

  return {
    state,
    load,
    setItem,
    setNote,
    commit: () => flush({ mayPrompt: true }),
    identify,
    declineIdentity,
    notMe,
    changeIdentity,
    itemOf,
    online,
    destroy,
  }
}
